/**
 * Stop Wondering Grid — interactive video grid.
 *
 * Default: 3x3 (desktop) / 1-col (mobile) grid of poster cards.
 * Desktop click: card expands to a tall left "home column"; the other cards
 *   reflow into the right two columns (CSS Grid). Video plays in place.
 * Mobile click: video plays full-screen, then fades back to the grid.
 * On the video `ended` event the card is marked "watched" (solid blue tile),
 *   persisted to sessionStorage so it stays watched while the session lasts.
 */
(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 768px)';

  // Parse a CSS aspect-ratio string like "9 / 16" into a width/height number.
  function parseRatio(value) {
    var parts = String(value).split('/');
    var w = parseFloat(parts[0]);
    var h = parseFloat(parts[1]);
    if (!w || !h) return 0.5625; // default 9:16
    return w / h;
  }

  function initSection(root) {
    if (!root || root.dataset.swInit === 'true') return;
    root.dataset.swInit = 'true';

    var grid = root.querySelector('[data-sw-grid]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-sw-card]'));
    var mql = window.matchMedia(DESKTOP_QUERY);
    var sectionId = root.dataset.sectionId || 'sw';
    var activeCard = null;
    var activeVideo = null;
    var videoHome = null;

    if (!grid || !cards.length) return;

    // Body-level fullscreen host so the mobile player stacks above the sticky
    // header and everything else (escapes the section's stacking context).
    var existingFs = document.querySelector('.sw-fs[data-sw-fs="' + sectionId + '"]');
    if (existingFs) existingFs.remove();
    var fs = document.createElement('div');
    fs.className = 'sw-fs';
    fs.dataset.swFs = sectionId;
    fs.hidden = true;
    var fsClose = document.createElement('button');
    fsClose.type = 'button';
    fsClose.className = 'sw-fs__close';
    fsClose.setAttribute('aria-label', 'Close video');
    fsClose.textContent = '×';
    fs.appendChild(fsClose);
    document.body.appendChild(fs);

    function storageKey(id) {
      return 'sw-watched:' + sectionId + ':' + id;
    }

    function markWatched(card) {
      card.classList.add('sw-card--watched');
      try {
        sessionStorage.setItem(storageKey(card.dataset.swCard), '1');
      } catch (e) {
        /* sessionStorage may be unavailable (private mode); state stays in-page only */
      }
    }

    function restoreWatched() {
      cards.forEach(function (card) {
        try {
          if (sessionStorage.getItem(storageKey(card.dataset.swCard)) === '1') {
            card.classList.add('sw-card--watched');
          }
        } catch (e) {
          /* ignore */
        }
      });
    }

    function deactivate() {
      if (!activeCard) return;
      var card = activeCard;
      var video = activeVideo;
      activeCard = null;
      activeVideo = null;

      if (video) {
        video.pause();
        video.removeAttribute('controls');
      }

      // Desktop layout reset.
      card.style.gridColumn = '';
      card.style.gridRow = '';
      grid.style.gridTemplateColumns = '';
      grid.classList.remove('sw-grid--active');
      card.classList.remove('sw-card--active');

      // Mobile portal teardown: move the video back into its card.
      if (video && videoHome && video.parentNode === fs) {
        video.classList.remove('sw-fs__video');
        videoHome.parent.insertBefore(video, videoHome.next);
      }
      videoHome = null;
      fs.hidden = true;
      document.body.classList.remove('sw-no-scroll');
    }

    function activate(card) {
      if (activeCard === card) return;
      deactivate();
      activeCard = card;

      var video = card.querySelector('[data-sw-video]');
      activeVideo = video;
      var isDesktop = mql.matches;

      if (isDesktop) {
        // Tall left column: span enough rows so the other cards fill the
        // right two columns exactly (2 columns -> ceil(others / 2) rows).
        var rows = Math.max(1, Math.ceil((cards.length - 1) / 2));
        card.style.gridColumn = '1';
        card.style.gridRow = '1 / span ' + rows;

        // Widen the left column so the video (its own aspect ratio) is as
        // tall as the `rows` of landscape cards beside it -> no empty gap.
        // leftFr = rows * (videoRatioW/H) / (cardRatioW/H), card aspect = 16/10.
        var videoRatio = parseRatio(
          getComputedStyle(root).getPropertyValue('--sw-active-ratio')
        );
        var leftFr = Math.max(1, rows * (videoRatio / 1.6));
        grid.style.gridTemplateColumns = leftFr.toFixed(3) + 'fr 1fr 1fr';

        grid.classList.add('sw-grid--active');
        card.classList.add('sw-card--active');
      } else {
        // Mobile: portal the video into the body-level fullscreen host.
        document.body.classList.add('sw-no-scroll');
        if (video) {
          videoHome = { parent: video.parentNode, next: video.nextSibling };
          video.classList.add('sw-fs__video');
          video.setAttribute('controls', '');
          fs.insertBefore(video, fsClose);
        }
        fs.hidden = false;
      }

      if (video) {
        try {
          video.currentTime = 0;
        } catch (e) {
          /* ignore — not always seekable before load */
        }
        var playback = video.play();
        if (playback && typeof playback.catch === 'function') {
          playback.catch(function () {
            /* autoplay rejected — controls (mobile) or the poster remain visible */
          });
        }
      }
    }

    cards.forEach(function (card) {
      var trigger = card.querySelector('[data-sw-trigger]');
      var video = card.querySelector('[data-sw-video]');

      if (trigger) {
        trigger.addEventListener('click', function (event) {
          event.stopPropagation();
          if (activeCard === card) {
            deactivate();
          } else {
            activate(card);
          }
        });

        // Warm up the video on hover (desktop) so playback starts promptly
        // when clicked. Runs once per card.
        if (video) {
          var preloaded = false;
          trigger.addEventListener('mouseenter', function () {
            if (preloaded || !mql.matches) return;
            preloaded = true;
            video.preload = 'auto';
            video.load();
          });
        }
      }

      if (video) {
        video.addEventListener('ended', function () {
          markWatched(card);
          deactivate();
        });
      }
    });

    fsClose.addEventListener('click', deactivate);
    fs.addEventListener('click', function (event) {
      // Close when tapping the dark area around the video.
      if (event.target === fs) deactivate();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') deactivate();
    });

    // Click outside collapses the expanded card (desktop only).
    document.addEventListener('click', function (event) {
      if (!activeCard || !mql.matches) return;
      if (!activeCard.contains(event.target)) deactivate();
    });

    // Collapse when crossing the breakpoint to avoid a stuck layout.
    var onBreakpointChange = function () {
      deactivate();
    };
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onBreakpointChange);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(onBreakpointChange);
    }

    restoreWatched();
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-sw-section]');
    Array.prototype.forEach.call(nodes, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor: re-init when this section is reloaded.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-sw-section]');
    if (root) {
      root.dataset.swInit = '';
      initSection(root);
    }
  });
})();
